import { cn } from '@heroui/react';
import type { FC, PointerEvent, ReactNode } from 'react';
import type { Block } from '@/components/canvas/blocks/types';
import type { BlockDisplayLayout } from '@/components/canvas/blocks/registry/types';
import { isBlockContentInteractive } from '@/components/canvas/blocks/registry/blockRegistry';

export interface BlockHostViewProps {
  block: Block;
  displayLayout: BlockDisplayLayout;
  contentSize: { width: number; height: number };
  /** 内容固有尺寸上的额外缩放（如 data.scale） */
  contentScale?: number;
  /** 缩放层外叠加层（如定时器进度环，避免 scale 导致 SVG 不重绘） */
  contentOverlay?: ReactNode;
  onPointerDown?: (e: PointerEvent, block: Block) => void;
  isPresentationMode?: boolean;
  children: ReactNode;
}

/** 块内可交互控件：不触发块选中/拖拽 */
function isBlockInnerControlTarget(el: HTMLElement): boolean {
  return !!el.closest(
    'button, a[href], input, textarea, select, [role="button"], [role="spinbutton"], [contenteditable="true"]',
  );
}

/** 功能块统一外壳：定位、命中、选框内 contain 缩放 */
export const BlockHostView: FC<BlockHostViewProps> = ({
  block,
  displayLayout,
  contentSize,
  contentScale = 1,
  contentOverlay,
  onPointerDown,
  isPresentationMode = false,
  children,
}) => {
  const { layout } = block;
  const fitScale =
    Math.min(
      displayLayout.width / Math.max(1, contentSize.width),
      displayLayout.height / Math.max(1, contentSize.height),
    ) * contentScale;

  const contentInteractive = isBlockContentInteractive(block.type);
  const editInteractive = !!onPointerDown;
  const outerPointerEvents =
    editInteractive || (contentInteractive && isPresentationMode);
  const overlaySize = {
    width: contentSize.width * fitScale,
    height: contentSize.height * fitScale,
  };

  return (
    <div
      className={cn(
        'absolute touch-none select-none',
        outerPointerEvents ? 'pointer-events-auto' : 'pointer-events-none',
      )}
      style={{
        left: displayLayout.x,
        top: displayLayout.y,
        width: displayLayout.width,
        height: displayLayout.height,
        zIndex: layout.zIndex,
        transform: layout.rotation ? `rotate(${layout.rotation}deg)` : undefined,
      }}
      data-block-hit={editInteractive ? true : undefined}
      data-block-id={block.id}
      onPointerDown={
        onPointerDown
          ? (e) => {
              if (e.button !== 0) return;
              if (contentInteractive && isBlockInnerControlTarget(e.target as HTMLElement)) {
                return;
              }
              e.stopPropagation();
              onPointerDown(e, block);
            }
          : undefined
      }
      onClick={editInteractive ? (e) => e.stopPropagation() : undefined}
    >
      <div
        className={cn(
          'flex size-full items-center justify-center overflow-hidden',
          contentInteractive ? 'pointer-events-auto' : 'pointer-events-none',
        )}
        data-block-content
      >
        <div
          className="relative inline-flex shrink-0"
          style={{
            width: contentSize.width,
            height: contentSize.height,
          }}
        >
          {contentOverlay ? (
            <div
              className="pointer-events-none absolute z-0"
              style={{
                width: overlaySize.width,
                height: overlaySize.height,
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              {contentOverlay}
            </div>
          ) : null}
          <div
            className="relative z-10 inline-flex"
            style={{
              width: contentSize.width,
              height: contentSize.height,
              transform:
                Math.abs(fitScale - 1) > 0.001 ? `scale(${fitScale})` : undefined,
              transformOrigin: 'center center',
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
